import React, { useEffect, useState, useCallback } from 'react';
import { Portal, IconButton, Typography, Box } from '@mui/material';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import ErrorRoundedIcon from '@mui/icons-material/ErrorRounded';
import WarningRoundedIcon from '@mui/icons-material/WarningRounded';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';

const DURATION = 4000;

const CONFIGS = {
  success: {
    Icon: CheckCircleRoundedIcon,
    gradient: 'linear-gradient(135deg, #064e3b 0%, #059669 100%)',
    barColor: '#6ee7b7',
    iconBg: 'rgba(110, 231, 183, 0.25)',
    label: 'Success',
  },
  error: {
    Icon: ErrorRoundedIcon,
    gradient: 'linear-gradient(135deg, #7f1d1d 0%, #ef4444 100%)',
    barColor: '#fca5a5',
    iconBg: 'rgba(252, 165, 165, 0.25)',
    label: 'Error',
  },
  warning: {
    Icon: WarningRoundedIcon,
    gradient: 'linear-gradient(135deg, #78350f 0%, #f59e0b 100%)',
    barColor: '#fcd34d',
    iconBg: 'rgba(252, 211, 77, 0.25)',
    label: 'Warning',
  },
  info: {
    Icon: InfoRoundedIcon,
    gradient: 'linear-gradient(135deg, #1B4332 0%, #1F7A54 100%)',
    barColor: '#93c5fd',
    iconBg: 'rgba(147, 197, 253, 0.25)',
    label: 'Info',
  },
};

export default function AppToast({ open, message, severity = 'success', onClose, autoHideDuration = DURATION }) {
  const [visible, setVisible] = useState(false);
  const [exiting, setExiting] = useState(false);
  const [animKey, setAnimKey] = useState(0);

  const cfg = CONFIGS[severity] || CONFIGS.info;
  const { Icon } = cfg;

  const handleClose = useCallback(() => {
    setExiting(true);
    setTimeout(() => {
      setExiting(false);
      setVisible(false);
      onClose && onClose();
    }, 320);
  }, [onClose]);

  useEffect(() => {
    if (open) {
      setVisible(true);
      setExiting(false);
      setAnimKey((k) => k + 1);
      const timer = setTimeout(() => handleClose(), autoHideDuration);
      return () => clearTimeout(timer);
    } else {
      setVisible(false);
      setExiting(false);
    }
  }, [open]);

  const displayMessage = (() => {
    if (!message && message !== 0) return '';
    if (typeof message === 'string') return message;
    if (typeof message === 'object') {
      const vals = Object.values(message).flat();
      return vals.length ? vals.join(' • ') : JSON.stringify(message);
    }
    return String(message);
  })();

  if (!visible) return null;

  return (
    <Portal>
      <Box
        sx={{
          position: 'fixed',
          bottom: 80,
          right: 24,
          zIndex: 9999,
          animation: exiting
            ? 'appToastOut 0.32s cubic-bezier(0.4, 0, 1, 1) forwards'
            : 'appToastIn 0.45s cubic-bezier(0.34, 1.56, 0.64, 1) forwards',
          '@keyframes appToastIn': {
            '0%': { transform: 'translateX(110%) scale(0.88)', opacity: 0 },
            '100%': { transform: 'translateX(0) scale(1)', opacity: 1 },
          },
          '@keyframes appToastOut': {
            '0%': { transform: 'translateX(0) scale(1)', opacity: 1 },
            '100%': { transform: 'translateX(110%) scale(0.88)', opacity: 0 },
          },
        }}
      >
        <Box
          sx={{
            width: 340,
            maxWidth: 'calc(100vw - 48px)',
            background: cfg.gradient,
            borderRadius: '14px',
            overflow: 'hidden',
            color: '#fff',
            boxShadow: '0 24px 64px rgba(0,0,0,0.32), 0 0 0 1px rgba(255,255,255,0.1) inset',
            backdropFilter: 'blur(12px)',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              alignItems: 'flex-start',
              gap: 1.5,
              px: 2,
              pt: 1.75,
              pb: 1.5,
            }}
          >
            <Box
              sx={{
                width: 38,
                height: 38,
                borderRadius: '50%',
                bgcolor: cfg.iconBg,
                border: '1.5px solid rgba(255,255,255,0.3)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mt: '1px',
                animation: 'appToastIconPop 0.5s cubic-bezier(0.34, 1.56, 0.64, 1) 0.1s both',
                '@keyframes appToastIconPop': {
                  '0%': { transform: 'scale(0)', opacity: 0 },
                  '100%': { transform: 'scale(1)', opacity: 1 },
                },
              }}
            >
              <Icon sx={{ fontSize: 20, color: '#fff' }} />
            </Box>

            <Box sx={{ flex: 1, minWidth: 0, pt: '2px' }}>
              <Typography
                sx={{
                  fontWeight: 700,
                  fontSize: '0.68rem',
                  textTransform: 'uppercase',
                  letterSpacing: '0.9px',
                  opacity: 0.7,
                  mb: '3px',
                  display: 'block',
                  fontFamily: 'inherit',
                }}
              >
                {cfg.label}
              </Typography>
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  lineHeight: 1.5,
                  wordBreak: 'break-word',
                  fontFamily: 'inherit',
                }}
              >
                {displayMessage}
              </Typography>
            </Box>

            <IconButton
              size="small"
              onClick={handleClose}
              sx={{
                color: 'rgba(255,255,255,0.7)',
                p: '4px',
                mt: '-2px',
                mr: '-4px',
                borderRadius: '8px',
                transition: 'all 0.15s ease',
                '&:hover': {
                  color: '#fff',
                  bgcolor: 'rgba(255,255,255,0.18)',
                  transform: 'scale(1.1)',
                },
              }}
            >
              <CloseRoundedIcon sx={{ fontSize: 17 }} />
            </IconButton>
          </Box>

          {/* Progress bar */}
          <Box sx={{ height: '3px', bgcolor: 'rgba(0,0,0,0.2)', position: 'relative', overflow: 'hidden' }}>
            <Box
              key={animKey}
              sx={{
                position: 'absolute',
                left: 0, top: 0, bottom: 0,
                bgcolor: cfg.barColor,
                boxShadow: `0 0 8px ${cfg.barColor}`,
                width: '100%',
                animation: `appToastProgress ${autoHideDuration}ms linear forwards`,
                '@keyframes appToastProgress': {
                  from: { width: '100%' },
                  to: { width: '0%' },
                },
              }}
            />
          </Box>
        </Box>
      </Box>
    </Portal>
  );
}
